import { Query } from '@apollo/client/react/components';
import { KeySet } from '@tableau/data-structures';
import React from 'react';

import {
  VirtualConnectionLineageCountsFilteredByTableQuery, VirtualConnectionLineageCountsFilteredByTableQueryVariables, VirtualConnectionLineageCountsQuery, VirtualConnectionLineageCountsQueryVariables,
  VirtualConnectionLineageCountsFilteredByTableDocument,
  VirtualConnectionLineageCountsDocument
} from '../../../../../gen-src/queries';
import { FeatureFlagsContext } from '../../../data-catalog-shared';
import { LineageCountsParentProps } from '../lineage/LineageCountsCacher';
import { renderFilteredLineageCounts, renderUnfilteredLineageCounts } from '../lineage/LineageCountsFunctions';


export type VirtualConnectionLineageCounts = {
  readonly filteredFieldName: string | null;
  readonly tables: number;
  readonly upstreamDatabases: number;
  readonly upstreamTables: number;
  readonly downstreamFlows: number;
  readonly downstreamDatasources: number;
  readonly downstreamLenses: number;
  readonly downstreamWorkbooks: number;
  readonly downstreamSheets: number;
  readonly downstreamDashboards: number;
  readonly downstreamMetrics: number;
  readonly downstreamOwners: number;
};

function createUnfilteredCounts(data: VirtualConnectionLineageCountsQuery): VirtualConnectionLineageCounts | null {
  const virtualConnection = data.virtualConnections && data.virtualConnections[0];
  if (virtualConnection
    && virtualConnection.unfilteredTablesConnection
    && virtualConnection.upstreamDatabasesConnection
    && virtualConnection.upstreamTablesConnection
    && virtualConnection.downstreamFlowsConnection
    && virtualConnection.downstreamDatasourcesConnection
    && virtualConnection.downstreamWorkbooksConnection
    && virtualConnection.downstreamSheetsConnection
    && virtualConnection.downstreamDashboardsConnection
    && virtualConnection.downstreamMetricsConnection
    && virtualConnection.downstreamOwnersConnection
  ) {
    return {
      filteredFieldName: null,
      tables: virtualConnection.unfilteredTablesConnection.totalCount,
      upstreamDatabases: virtualConnection.upstreamDatabasesConnection.totalCount,
      upstreamTables: virtualConnection.upstreamTablesConnection.totalCount,
      downstreamFlows: virtualConnection.downstreamFlowsConnection.totalCount,
      downstreamDatasources: virtualConnection.downstreamDatasourcesConnection.totalCount,
      downstreamWorkbooks: virtualConnection.downstreamWorkbooksConnection.totalCount,
      downstreamLenses: virtualConnection.downstreamLensesConnection?.totalCount || 0,
      downstreamSheets: virtualConnection.downstreamSheetsConnection.totalCount,
      downstreamDashboards: virtualConnection.downstreamDashboardsConnection.totalCount,
      downstreamMetrics: virtualConnection.downstreamMetricsConnection.totalCount,
      downstreamOwners: virtualConnection.downstreamOwnersConnection.totalCount,
    };
  }
  return null;
}

function createFilteredCounts(data: VirtualConnectionLineageCountsFilteredByTableQuery): VirtualConnectionLineageCounts | null {
  const virtualConnection = data.virtualConnections && data.virtualConnections[0];
  const filteredTable = virtualConnection && virtualConnection.filteredTablesConnection && virtualConnection.filteredTablesConnection.nodes[0];
  if (virtualConnection
    && virtualConnection.unfilteredTablesConnection
    && filteredTable
    && filteredTable.upstreamDatabasesConnection
    && filteredTable.upstreamTablesConnection
    && filteredTable.downstreamFlowsConnection
    && filteredTable.downstreamDatasourcesConnection
    && filteredTable.downstreamWorkbooksConnection
    && filteredTable.downstreamSheetsConnection
    && filteredTable.downstreamDashboardsConnection
    && filteredTable.downstreamMetricsConnection
    && filteredTable.downstreamOwnersConnection
  ) {
    return {
      filteredFieldName: filteredTable?.name ?? null,
      tables: virtualConnection.unfilteredTablesConnection.totalCount,
      upstreamDatabases: filteredTable.upstreamDatabasesConnection.totalCount,
      upstreamTables: filteredTable.upstreamTablesConnection.totalCount,
      downstreamFlows: filteredTable.downstreamFlowsConnection.totalCount,
      downstreamDatasources: filteredTable.downstreamDatasourcesConnection.totalCount,
      downstreamLenses: filteredTable.downstreamLensesConnection?.totalCount || 0,
      downstreamWorkbooks: filteredTable.downstreamWorkbooksConnection.totalCount,
      downstreamSheets: filteredTable.downstreamSheetsConnection.totalCount,
      downstreamDashboards: filteredTable.downstreamDashboardsConnection.totalCount,
      downstreamMetrics: filteredTable.downstreamMetricsConnection.totalCount,
      downstreamOwners: filteredTable.downstreamOwnersConnection.totalCount
    };
  }
  return null;
}

export type VirtualConnectionLineageCountsQueryWrapperProps = {
  readonly vizportalUrlId: string;
  readonly selectedTableKeys: KeySet;
} & LineageCountsParentProps<VirtualConnectionLineageCounts>;

export class VirtualConnectionLineageCountsQueryWrapper extends React.Component<VirtualConnectionLineageCountsQueryWrapperProps> {
  public override render(): React.ReactNode {
    const selectedFields = Object.keys(this.props.selectedTableKeys);
    if (selectedFields.length > 0) {
      const selectedTableId = selectedFields[0];
      return (
        <FeatureFlagsContext.Consumer>
          {() =>
            <Query<VirtualConnectionLineageCountsFilteredByTableQuery, VirtualConnectionLineageCountsFilteredByTableQueryVariables>
              query={VirtualConnectionLineageCountsFilteredByTableDocument}
              variables={{
                vizportalUrlId: this.props.vizportalUrlId,
                selectedTableId: selectedTableId,
              }}
            >
              {result => <>{renderFilteredLineageCounts(result, createUnfilteredCounts, createFilteredCounts, this.props.children)}</>}
            </Query>}
        </FeatureFlagsContext.Consumer >
      );
    } else {
      return (
        <FeatureFlagsContext.Consumer>
          {() =>
            <Query<VirtualConnectionLineageCountsQuery, VirtualConnectionLineageCountsQueryVariables>
              query={VirtualConnectionLineageCountsDocument}
              variables={{
                vizportalUrlId: this.props.vizportalUrlId,
              }}
            >
              {result => <>{renderUnfilteredLineageCounts(result, createUnfilteredCounts, this.props.children)}</>}
            </Query>}
        </FeatureFlagsContext.Consumer>
      );
    }
  }

}

